import { useEffect, useState } from "react";
import { Loader2, Wallet, AlertTriangle, Info, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { rupiah, fmtDate } from "@/lib/format";
import { AgingBadge } from "@/components/finance/aging";

function useCustomerAr(customerId) {
  const [ar, setAr] = useState(null);
  useEffect(() => {
    if (customerId == null) return;
    setAr(null);
    api.get(`/customers/${customerId}/ar`).then((r) => setAr(r.data.data)).catch(() => setAr({ error: true }));
  }, [customerId]);
  return ar;
}

export function CustomerArCard({ customerId }) {
  const ar = useCustomerAr(customerId);
  if (!ar) return <div className="ar-card" data-testid="customer-ar-card"><Loader2 className="spin" size={16} /> Memuat kartu piutang…</div>;
  if (ar.error) return null;
  const overdue = ar.overdue_amount > 0;
  return (
    <div className={`ar-card ${overdue ? "overdue" : ""}`} data-testid="customer-ar-card">
      <div className="ar-card-head"><Wallet size={16} /><strong>Kartu Piutang</strong><small>Termin {ar.ar_term_name}</small></div>
      <div className="ar-card-stats">
        <div><span>Sisa tagihan</span><b data-testid="customer-ar-outstanding">{rupiah(ar.outstanding)}</b><small>{ar.open_count} invoice terbuka</small></div>
        <div className={overdue ? "bad" : ""}><span>Lewat jatuh tempo</span><b data-testid="customer-ar-overdue">{rupiah(ar.overdue_amount)}</b>
          <small>{overdue ? `${ar.overdue_count} invoice · terlama ${ar.max_days_overdue} hari` : "Tidak ada"}</small></div>
        <div><span>Jatuh tempo berikutnya</span><b>{ar.next_due ? fmtDate(ar.next_due.due_date) : "—"}</b>
          <small>{ar.next_due ? `${ar.next_due.number} · ${rupiah(ar.next_due.amount_residual)}` : "—"}</small></div>
      </div>
      {ar.credit_limit > 0 && (
        <div className={`ar-credit-bar ${ar.available_credit < 0 ? "over" : ""}`} data-testid="customer-ar-credit">
          <div><span>Batas kredit {rupiah(ar.credit_limit)}</span>
            <span>Terpakai {rupiah(ar.exposure)} (tagihan {rupiah(ar.outstanding)} + order belum ditagih {rupiah(ar.unbilled_orders)})</span>
            <b data-testid="customer-ar-available">{ar.available_credit < 0 ? `Lewat ${rupiah(-ar.available_credit)}` : `Sisa ${rupiah(ar.available_credit)}`}</b></div>
          <i><em style={{ width: `${Math.min(100, Math.round((ar.exposure / ar.credit_limit) * 100))}%` }} /></i>
        </div>
      )}
      {ar.invoices.length > 0 && (
        <div className="ar-card-list" data-testid="customer-ar-invoices">
          {ar.invoices.slice(0, 6).map((inv) => (
            <div key={inv.id} data-testid={`customer-ar-inv-${inv.number}`}>
              <span><b>{inv.number}</b><small>{inv.so_number} · JT {fmtDate(inv.due_date)}</small></span>
              <AgingBadge inv={inv} /><b>{rupiah(inv.amount_residual)}</b>
            </div>
          ))}
          {ar.invoices.length > 6 && <small className="fin-muted">+{ar.invoices.length - 6} invoice lainnya di menu Finance</small>}
        </div>
      )}
    </div>
  );
}

export function CustomerArWarning({ customerId, orderAmount = 0 }) {
  const ar = useCustomerAr(customerId);
  if (!ar || ar.error) return null;
  const limit = ar.credit_limit || 0;
  const after = (ar.exposure || 0) + (orderAmount || 0);
  const overLimit = limit > 0 && after > limit;
  const credit = overLimit && (
    <div className="ar-warning bad" data-testid="order-credit-limit-warning">
      <ShieldAlert size={16} />
      <span><b>Order ini membuat total tagihan customer melewati batas kredit.</b> Batas {rupiah(limit)} · tagihan berjalan + order belum ditagih {rupiah(ar.exposure)} · setelah order ini {rupiah(after)} (lebih {rupiah(after - limit)}).
        Order tetap tersimpan, tetapi harus disetujui Finance di menu Approval sebelum diproses & dikirim ke Odoo.</span>
    </div>
  );
  if (ar.outstanding <= 0) return credit || null;
  if (ar.overdue_amount > 0) {
    return (
      <>
        {credit}
        <div className="ar-warning bad" data-testid="order-ar-warning">
          <AlertTriangle size={16} />
          <span><b>Customer punya tagihan lewat jatuh tempo {rupiah(ar.overdue_amount)}</b> ({ar.overdue_count} invoice, terlama {ar.max_days_overdue} hari).
            Total sisa tagihan {rupiah(ar.outstanding)}. Order tetap bisa dibuat — mohon konfirmasi pembayaran ke customer.</span>
        </div>
      </>
    );
  }
  return (
    <>
      {credit}
      <div className="ar-warning" data-testid="order-ar-info">
        <Info size={16} /><span>Sisa tagihan berjalan {rupiah(ar.outstanding)} ({ar.open_count} invoice, belum jatuh tempo).</span>
      </div>
    </>
  );
}
